import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { STATUSES, statusMeta, sourceIcon, fmtMoney } from "../lib/constants";
import LeadDrawer from "../components/LeadDrawer";
import SavedSegments from "../components/SavedSegments";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { motion } from "framer-motion";

export default function Pipeline() {
  const qc = useQueryClient();
  const [drawerId, setDrawerId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const [category, setCategory] = useState("all");
  const [location, setLocation] = useState("all");

  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.get("/leads").then((r) => r.data),
  });

  const { data: facets = { categories: [], locations: [] } } = useQuery({
    queryKey: ["lead-facets"],
    queryFn: () => api.get("/leads/facets").then((r) => r.data),
  });

  const matchLoc = (l) => {
    if (location === "all") return true;
    if (location === "__unknown__") return !l.city;
    const [c, p] = location.split("|");
    return (!c || l.city === c) && (!p || l.province === p);
  };
  const filtered = leads.filter(
    (l) => (category === "all" || (l.category || "Uncategorized") === category) && matchLoc(l)
  );

  const applySegment = (f) => {
    setCategory(f.category || "all");
    setLocation(f.location || "all");
  };

  const onDrop = async (status) => {
    setOverCol(null);
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    if (!lead || lead.status === status) { setDragId(null); return; }
    qc.setQueryData(["leads"], (old) =>
      (old || []).map((l) => (l.id === dragId ? { ...l, status } : l))
    );
    try {
      await api.patch(`/leads/${dragId}/status`, { status });
      toast.success(`Moved to ${statusMeta(status).label}`);
    } catch (e) {
      qc.invalidateQueries({ queryKey: ["leads"] });
    }
    setDragId(null);
  };

  return (
    <div data-testid="pipeline-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Pipeline</h1>
          <p className="text-muted-foreground mt-1">Drag leads across stages to move deals forward.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="sm:w-48" data-testid="pipeline-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-popover border-border max-h-72">
            <SelectItem value="all">All categories</SelectItem>
            {facets.categories.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.value} ({c.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="sm:w-52" data-testid="pipeline-location-filter"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent className="bg-popover border-border max-h-72">
            <SelectItem value="all">All locations</SelectItem>
            {facets.locations.map((l) => (
              <SelectItem key={l.value} value={l.value}>{l.label} ({l.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <SavedSegments filters={{ q: "", status: "all", source: "all", category, location }} onApply={applySegment} />

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
        {STATUSES.map((col) => {
          const items = filtered.filter((l) => l.status === col.key);
          const total = items.reduce((sum, l) => sum + (l.deal_value || 0), 0);
          return (
            <div
              key={col.key}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
              onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
              onDrop={() => onDrop(col.key)}
              className={`w-72 shrink-0 rounded-2xl border ${overCol === col.key ? "border-primary bg-primary/5" : "border-border bg-card/40"} transition-colors`}
              data-testid={`pipeline-column-${col.key}`}
            >
              <div className="sticky top-0 p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-2 font-semibold ${col.text}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
                    {col.label}
                  </span>
                  <span className="text-xs text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{items.length}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{fmtMoney(total)}</p>
              </div>
              <div className="p-3 space-y-3 min-h-[120px]">
                {items.map((lead) => {
                  const Icon = sourceIcon(lead.source);
                  return (
                    <motion.div
                      layout
                      key={lead.id}
                      draggable
                      onDragStart={() => setDragId(lead.id)}
                      onClick={() => setDrawerId(lead.id)}
                      whileHover={{ y: -2 }}
                      className={`cursor-pointer rounded-xl border border-border bg-card p-4 shadow-sm hover:border-primary/40 ${dragId === lead.id ? "opacity-50 scale-105" : ""}`}
                      data-testid={`lead-card-${lead.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm">{lead.name}</p>
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      </div>
                      {lead.company && <p className="text-xs text-muted-foreground mt-0.5">{lead.company}</p>}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-emerald-400 font-medium">{fmtMoney(lead.deal_value)}</span>
                        {lead.phone && <span className="text-[10px] text-muted-foreground font-mono">{lead.phone}</span>}
                      </div>
                    </motion.div>
                  );
                })}
                {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Drop leads here</p>}
              </div>
            </div>
          );
        })}
      </div>

      <LeadDrawer leadId={drawerId} open={!!drawerId} onOpenChange={(v) => !v && setDrawerId(null)} />
    </div>
  );
}
